import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ExternalLink } from "lucide-react";
import { createClient, updateClient } from "@/utils/api";
import { toast } from "sonner";

const FREQUENCY_OPTIONS = [
  { value: "1", label: "Ogni settimana" },
  { value: "2", label: "Ogni 2 settimane" },
  { value: "3", label: "Ogni 3 settimane" },
  { value: "4", label: "Ogni 4 settimane (mensile)" },
  { value: "6", label: "Ogni 6 settimane" },
  { value: "8", label: "Ogni 8 settimane (bimestrale)" },
  { value: "12", label: "Ogni 12 settimane (trimestrale)" },
];

const DAY_OPTIONS = [
  { value: "none", label: "Nessuna preferenza" },
  { value: "0", label: "Lunedì" },
  { value: "1", label: "Martedì" },
  { value: "2", label: "Mercoledì" },
  { value: "3", label: "Giovedì" },
  { value: "4", label: "Venerdì" },
  { value: "5", label: "Sabato" },
  { value: "6", label: "Domenica" },
];

const SELECT_CLASS = "mt-1 h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

const DEFAULT_FORM = {
  name: "",
  company: "",
  address: "",
  city: "",
  phone: "",
  email: "",
  zone: "",
  frequency_weeks: "2",
  preferred_day: "none",
  first_visit_date: "",
  first_visit_time: "",
  notes: "",
  coords: "",
};

export default function ClientForm({ open, onClose, onSuccess, client }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const isEdit = !!client;

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        company: client.company || "",
        address: client.address || "",
        city: client.city || "",
        phone: client.phone || "",
        email: client.email || "",
        zone: client.zone || "",
        frequency_weeks: String(client.frequency_weeks || 2),
        preferred_day: client.preferred_day != null ? String(client.preferred_day) : "none",
        notes: client.notes || "",
        coords: (client.lat && client.lng) ? `${client.lat}, ${client.lng}` : "",
      });
    } else {
      setForm(DEFAULT_FORM);
    }
  }, [client, open]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company.trim() && !form.name.trim()) {
      toast.error("Inserisci almeno il nome azienda o il nome del cliente");
      return;
    }
    setLoading(true);
    try {
      // Parsa coordinate se inserite (formato "lat, lng")
      let lat = null, lng = null;
      if (form.coords.trim()) {
        const parts = form.coords.split(",").map(s => parseFloat(s.trim()));
        if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
          lat = parts[0]; lng = parts[1];
        } else {
          toast.error("Formato coordinate non valido. Usa: 45.4654, 9.1859");
          setLoading(false);
          return;
        }
      }
      const payload = {
        ...form,
        frequency_weeks: parseInt(form.frequency_weeks),
        preferred_day: form.preferred_day !== "none" ? parseInt(form.preferred_day) : null,
        first_visit_date: form.first_visit_date || null,
        first_visit_time: form.first_visit_time || null,
        lat,
        lng,
      };
      delete payload.coords;
      // Se non c'è nome, usa l'azienda come nome
      if (!payload.name.trim()) payload.name = payload.company;
      if (isEdit) {
        await updateClient(client.id, payload);
        toast.success("Cliente aggiornato");
      } else {
        await createClient(payload);
        toast.success("Cliente aggiunto");
      }
      onClose();
      onSuccess?.();
    } catch (err) {
      toast.error("Errore nel salvataggio del cliente");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="client-form-modal" className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#0F172A]">
            {isEdit ? "Modifica Cliente" : "Nuovo Cliente"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label htmlFor="client-company">Azienda *</Label>
              <Input
                id="client-company"
                data-testid="client-company-input"
                placeholder="Rossi SRL"
                value={form.company}
                onChange={set("company")}
                className="mt-1"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="client-name">Nome e Cognome</Label>
              <Input
                id="client-name"
                data-testid="client-name-input"
                placeholder="Mario Rossi"
                value={form.name}
                onChange={set("name")}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="client-phone">Telefono</Label>
              <Input
                id="client-phone"
                data-testid="client-phone-input"
                placeholder="+39 02 1234567"
                value={form.phone}
                onChange={set("phone")}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="client-email">Email</Label>
              <Input
                id="client-email"
                data-testid="client-email-input"
                type="email"
                placeholder="email@esempio.it"
                value={form.email}
                onChange={set("email")}
                className="mt-1"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="client-address">Indirizzo</Label>
              <Input
                id="client-address"
                data-testid="client-address-input"
                placeholder="Via Roma, 1"
                value={form.address}
                onChange={set("address")}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="client-city">Città</Label>
              <Input
                id="client-city"
                data-testid="client-city-input"
                placeholder="Milano"
                value={form.city}
                onChange={set("city")}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="client-zone">Zona</Label>
              <Input
                id="client-zone"
                data-testid="client-zone-input"
                placeholder="Nord-Ovest"
                value={form.zone}
                onChange={set("zone")}
                className="mt-1"
              />
            </div>

            {/* Bottone Google Maps — cerca nome+azienda+via+città */}
            {(form.address || form.city) && (() => {
              const q = [form.company, form.name, form.address, form.city].filter(Boolean).join(" ");
              const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
              return (
                <div className="col-span-2">
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="gmaps-search-btn"
                    className="flex items-center justify-center gap-2 w-full py-2 rounded-lg border border-blue-200 bg-blue-50 text-[#2563EB] text-sm font-medium hover:bg-blue-100 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Verifica posizione su Google Maps
                  </a>
                  <p className="text-[11px] text-slate-400 mt-1 text-center">
                    Apre Google Maps con: <span className="text-slate-600 font-medium">{[form.company, form.name, form.address, form.city].filter(Boolean).join(", ")}</span>
                  </p>
                </div>
              );
            })()}

            {/* Campo coordinate manuali */}
            <div className="col-span-2">
              <Label htmlFor="client-coords">
                Coordinate precise <span className="text-slate-400 font-normal">(opzionale)</span>
              </Label>
              <Input
                id="client-coords"
                data-testid="client-coords-input"
                placeholder="es. 45.4654, 9.1859"
                value={form.coords}
                onChange={set("coords")}
                className="mt-1 font-mono text-sm"
              />
              <p className="text-[11px] text-slate-400 mt-1 leading-tight">
                Su Google Maps: clic destro sul punto esatto → tocca le coordinate che compaiono in cima → si copiano.
                {form.coords && !form.coords.split(",").map(s => parseFloat(s.trim())).every((v, i, a) => a.length === 2 && !isNaN(v)) && (
                  <span className="text-red-400 ml-1">Formato non valido</span>
                )}
              </p>
            </div>

            <div className="col-span-2">
              <Label htmlFor="frequency-select">Frequenza Visita</Label>
              <select
                id="frequency-select"
                data-testid="frequency-select"
                value={form.frequency_weeks}
                onChange={set("frequency_weeks")}
                className={SELECT_CLASS}
              >
                {FREQUENCY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="preferred-day-select">Giorno di Visita</Label>
              <select
                id="preferred-day-select"
                data-testid="preferred-day-select"
                value={form.preferred_day}
                onChange={set("preferred_day")}
                className={SELECT_CLASS}
              >
                {DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {form.preferred_day !== "none" && (
                <p className="text-xs text-blue-600 mt-1">
                  Le visite verranno sempre programmate di {DAY_OPTIONS.find(d => d.value === form.preferred_day)?.label}
                </p>
              )}
            </div>

            {/* Data e ora prima visita — solo in creazione */}
            {!isEdit && (
              <div className="col-span-2">
                <Label>Data Prima Visita</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    data-testid="first-visit-date-input"
                    type="date"
                    value={form.first_visit_date}
                    onChange={set("first_visit_date")}
                    className="flex-1"
                  />
                  <Input
                    data-testid="first-visit-time-input"
                    type="time"
                    value={form.first_visit_time}
                    onChange={set("first_visit_time")}
                    className="w-28"
                    placeholder="09:00"
                  />
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {form.first_visit_date
                    ? `${new Date(form.first_visit_date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}${form.first_visit_time ? " alle " + form.first_visit_time : ""}`
                    : form.preferred_day !== "none"
                      ? "Se non specificata, verrà usato il prossimo giorno preferito"
                      : "Opzionale — imposta data e ora del primo appuntamento"}
                </p>
              </div>
            )}

            <div className="col-span-2">
              <Label htmlFor="client-notes">Note</Label>
              <Textarea
                id="client-notes"
                data-testid="client-notes-input"
                placeholder="Note sul cliente (preferenze, orari, ecc.)"
                value={form.notes}
                onChange={set("notes")}
                rows={2}
                className="mt-1 resize-none"
              />
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="client-form-cancel"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-[#0F172A] hover:bg-[#1E293B] text-white"
              data-testid="client-form-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Salva" : "Aggiungi"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
